import { useState, useEffect } from 'react';
import { getSchedulesForAttendance, getStudentsForSchedule, recordAttendanceBatch, getAttendanceAlerts } from '../../../hooks/controlEscolarService';
import type { ScheduleForAttendance, StudentForAttendance, AttendanceStatus, AttendanceAlert } from '../../../hooks/controlEscolarTypes';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';

interface AcademicPeriod {
  id: number;
  code: string;
  name: string;
  type: string;
  status: string;
  startDate: string | null;
  endDate: string | null;
}

const formatDateToDDMMYYYY = (dateStr: string | null | undefined): string => {
  if (!dateStr) return 'Sin fecha';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export default function RegistroAsistencia() {
  const { toast } = useToast();
  const [periods, setPeriods] = useState<AcademicPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number | null>(null);
  const [schedules, setSchedules] = useState<ScheduleForAttendance[]>([]);
  const [students, setStudents] = useState<StudentForAttendance[]>([]);
  const [alerts, setAlerts] = useState<AttendanceAlert[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<number | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(true);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [saving, setSaving] = useState(false);
  const [view, setView] = useState<'register' | 'alerts'>('register');
  
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [isGroupDropdownOpen, setIsGroupDropdownOpen] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState<number | null>(null);
  const [courseSearchTerm, setCourseSearchTerm] = useState('');
  const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
  const [classSearchTerm, setClassSearchTerm] = useState('');
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);

  useEffect(() => {
    loadPeriods();
    loadAlerts();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      loadSchedules(selectedPeriod);
      setSelectedCourseId(null);
      setCourseSearchTerm('');
      setSelectedSchedule(null);
      setClassSearchTerm('');
      setStudents([]);
    } else {
      setSchedules([]);
      setSelectedCourseId(null);
      setCourseSearchTerm('');
      setSelectedSchedule(null);
      setClassSearchTerm('');
      setStudents([]);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    setSelectedSchedule(null);
    setClassSearchTerm('');
    setStudents([]);
  }, [selectedCourseId]);

  useEffect(() => {
    if (selectedSchedule) {
      loadStudents();
    }
  }, [selectedSchedule, selectedDate]);

  const loadPeriods = async () => {
    try {
      setLoadingPeriods(true);
      const response = await fetch('/api/attendance/periods', {
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Error al cargar períodos');
      const data = await response.json();
      setPeriods(data);
    } catch (error) {
      console.error('Error cargando períodos:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los grupos académicos',
      });
    } finally {
      setLoadingPeriods(false);
    }
  };

  const loadSchedules = async (periodId: number) => {
    try {
      setLoadingSchedules(true);
      const data = await getSchedulesForAttendance(periodId);
      setSchedules(data);
    } catch (error) {
      console.error('Error cargando horarios:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los horarios',
      });
    } finally {
      setLoadingSchedules(false);
    }
  };

  const loadStudents = async () => {
    if (!selectedSchedule) return;
    try {
      setLoading(true);
      const data = await getStudentsForSchedule(selectedSchedule, selectedDate);
      setStudents(data);
    } catch (error) {
      console.error('Error cargando estudiantes:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'No se pudieron cargar los estudiantes',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const data = await getAttendanceAlerts();
      setAlerts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando alertas:', error);
      setAlerts([]);
    }
  };

  const handleAttendanceChange = (alumnoId: number, status: AttendanceStatus) => {
    setStudents(prev =>
      prev.map(student =>
        student.id === alumnoId
          ? {
              ...student,
              attendance: {
                ...(student.attendance || { id: 0, date: selectedDate, createdAt: '' }),
                status,
              },
            }
          : student
      )
    );
  };

  const handleSaveAttendance = async () => {
    if (!selectedSchedule) return;

    const records = students.map(student => ({
      alumnoId: student.id,
      status: student.attendance?.status || 'presente',
      comments: student.attendance?.comments,
    }));

    try {
      setSaving(true);
      await recordAttendanceBatch({
        scheduleId: selectedSchedule,
        date: selectedDate,
        records,
      });
      toast({
        title: 'Éxito',
        description: `Asistencia registrada para ${records.length} estudiantes`,
      });
      loadStudents();
      loadAlerts();
    } catch (error: any) {
      console.error('Error guardando asistencia:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'No se pudo registrar la asistencia',
      });
    } finally {
      setSaving(false);
    }
  };

  const selectedScheduleData = schedules.find(s => s.id === selectedSchedule);
  const selectedPeriodData = periods.find(p => p.id === selectedPeriod);

  const getAlertLevel = (absences: number): 'critical' | 'warning' | 'info' => {
    if (absences >= 5) return 'critical';
    if (absences >= 3) return 'warning';
    return 'info';
  };

  const alertStyles = {
    critical: 'bg-red-50 border-red-500 text-red-900',
    warning: 'bg-yellow-50 border-yellow-500 text-yellow-900',
    info: 'bg-blue-50 border-blue-500 text-blue-900',
  };

  const alertIcons = {
    critical: '🚨',
    warning: '⚠️',
    info: 'ℹ️',
  };

  const periodTypeLabels: Record<string, string> = {
    matutino: 'Matutino',
    vespertino: 'Vespertino',
    sabatino: 'Sabatino',
  };

  const filteredPeriods = periods.filter(period => {
    const searchLower = groupSearchTerm.toLowerCase();
    return (
      period.name.toLowerCase().includes(searchLower) ||
      period.code.toLowerCase().includes(searchLower) ||
      (period.type && period.type.toLowerCase().includes(searchLower))
    );
  });

  const selectedPeriodForDisplay = periods.find(p => p.id === selectedPeriod);

  const getClassDisplayLabel = (schedule: ScheduleForAttendance): string => {
    const sessionLabel = schedule.sessionNumber && schedule.totalSessions
      ? `Clase ${schedule.sessionNumber} de ${schedule.totalSessions} - `
      : '';
    return `${sessionLabel}${schedule.courseName} - ${formatDateToDDMMYYYY(schedule.startDate)} (${schedule.dia} ${schedule.horaInicio}-${schedule.horaFin})`;
  };

  const uniqueCourses = schedules.reduce<{ courseId: number; courseName: string; courseCode: string }[]>((acc, s) => {
    if (!acc.find(c => c.courseId === s.courseId)) {
      acc.push({ courseId: s.courseId, courseName: s.courseName, courseCode: s.courseCode });
    }
    return acc;
  }, []).sort((a, b) => a.courseName.localeCompare(b.courseName));

  const filteredCourses = uniqueCourses.filter(c => {
    const searchLower = courseSearchTerm.toLowerCase();
    return (
      c.courseName.toLowerCase().includes(searchLower) ||
      c.courseCode.toLowerCase().includes(searchLower)
    );
  });

  const selectedCourseForDisplay = uniqueCourses.find(c => c.courseId === selectedCourseId);

  const schedulesForSelectedCourse = selectedCourseId
    ? schedules.filter(s => s.courseId === selectedCourseId)
    : schedules;

  const filteredSchedules = schedulesForSelectedCourse.filter(schedule => {
    const searchLower = classSearchTerm.toLowerCase();
    const label = getClassDisplayLabel(schedule).toLowerCase();
    return (
      label.includes(searchLower) ||
      schedule.courseCode.toLowerCase().includes(searchLower) ||
      schedule.courseName.toLowerCase().includes(searchLower) ||
      (schedule.professorName && schedule.professorName.toLowerCase().includes(searchLower))
    );
  });

  const selectedScheduleForDisplay = schedules.find(s => s.id === selectedSchedule);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Registro de Asistencia</h1>
        <p className="text-gray-600">Seguimiento y control de asistencia estudiantil</p>
      </div>

      <div className="mb-6 flex gap-2 border-b">
        <button
          onClick={() => setView('register')}
          className={`px-4 py-2 font-medium ${
            view === 'register'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Registrar Asistencia
        </button>
        <button
          onClick={() => setView('alerts')}
          className={`px-4 py-2 font-medium ${
            view === 'alerts'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Alertas de Ausentismo ({alerts.length})
        </button>
      </div>

      {view === 'register' ? (
        <>
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  1. Seleccionar Grupo Académico
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={loadingPeriods ? "Cargando grupos..." : "Buscar por clave o nombre..."}
                    value={isGroupDropdownOpen ? groupSearchTerm : (selectedPeriodForDisplay ? `${selectedPeriodForDisplay.code} - ${selectedPeriodForDisplay.name}` : '')}
                    onChange={(e) => {
                      setGroupSearchTerm(e.target.value);
                      if (!isGroupDropdownOpen) setIsGroupDropdownOpen(true);
                    }}
                    onFocus={() => {
                      setIsGroupDropdownOpen(true);
                      if (selectedPeriod) {
                        setGroupSearchTerm('');
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setIsGroupDropdownOpen(false), 200);
                    }}
                    disabled={loadingPeriods}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    🔍
                  </span>
                </div>
                
                {isGroupDropdownOpen && !loadingPeriods && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredPeriods.length > 0 ? (
                      filteredPeriods.map(period => (
                        <div
                          key={period.id}
                          className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selectedPeriod === period.id ? 'bg-blue-100' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedPeriod(period.id);
                            setGroupSearchTerm('');
                            setIsGroupDropdownOpen(false);
                          }}
                        >
                          <div className="font-medium">{period.code} - {period.name}</div>
                          <div className="text-xs text-gray-500">
                            {periodTypeLabels[period.type] || period.type}
                            {period.startDate && ` | ${formatDateToDDMMYYYY(period.startDate)} - ${formatDateToDDMMYYYY(period.endDate)}`}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No se encontraron grupos con "{groupSearchTerm}"
                      </div>
                    )}
                  </div>
                )}
                
                {selectedPeriod && !isGroupDropdownOpen && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedPeriod(null);
                      setGroupSearchTerm('');
                    }}
                    className="absolute right-8 top-[38px] text-gray-400 hover:text-red-500"
                    title="Limpiar selección"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  2. Filtrar por Materia
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={!selectedPeriod ? "Primero seleccione un grupo" : loadingSchedules ? "Cargando materias..." : uniqueCourses.length === 0 ? "Sin materias disponibles" : "Buscar materia..."}
                    value={isCourseDropdownOpen ? courseSearchTerm : (selectedCourseForDisplay ? `${selectedCourseForDisplay.courseCode} - ${selectedCourseForDisplay.courseName}` : '')}
                    onChange={(e) => {
                      setCourseSearchTerm(e.target.value);
                      if (!isCourseDropdownOpen) setIsCourseDropdownOpen(true);
                    }}
                    onFocus={() => {
                      setIsCourseDropdownOpen(true);
                      if (selectedCourseId) setCourseSearchTerm('');
                    }}
                    onBlur={() => {
                      setTimeout(() => setIsCourseDropdownOpen(false), 200);
                    }}
                    disabled={!selectedPeriod || loadingSchedules || uniqueCourses.length === 0}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8 disabled:bg-gray-50"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    🔍
                  </span>
                </div>

                {isCourseDropdownOpen && !loadingSchedules && selectedPeriod && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredCourses.length > 0 ? (
                      filteredCourses.map(course => (
                        <div
                          key={course.courseId}
                          className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selectedCourseId === course.courseId ? 'bg-blue-100' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedCourseId(course.courseId);
                            setCourseSearchTerm('');
                            setIsCourseDropdownOpen(false);
                          }}
                        >
                          <div className="font-medium">{course.courseName}</div>
                          <div className="text-xs text-gray-500">{course.courseCode}</div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No se encontraron materias con "{courseSearchTerm}"
                      </div>
                    )}
                  </div>
                )}

                {selectedCourseId && !isCourseDropdownOpen && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedCourseId(null);
                      setCourseSearchTerm('');
                    }}
                    className="absolute right-8 top-[38px] text-gray-400 hover:text-red-500"
                    title="Limpiar selección"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  3. Seleccionar Clase
                </label>
                <div className="relative">
                  <input
                    type="text"
                    placeholder={!selectedPeriod ? "Primero seleccione un grupo" : !selectedCourseId ? "Primero seleccione una materia" : loadingSchedules ? "Cargando clases..." : schedulesForSelectedCourse.length === 0 ? "Sin clases para esta materia" : "Buscar por fecha o profesor..."}
                    value={isClassDropdownOpen ? classSearchTerm : (selectedScheduleForDisplay ? getClassDisplayLabel(selectedScheduleForDisplay) : '')}
                    onChange={(e) => {
                      setClassSearchTerm(e.target.value);
                      if (!isClassDropdownOpen) setIsClassDropdownOpen(true);
                    }}
                    onFocus={() => {
                      setIsClassDropdownOpen(true);
                      if (selectedSchedule) {
                        setClassSearchTerm('');
                      }
                    }}
                    onBlur={() => {
                      setTimeout(() => setIsClassDropdownOpen(false), 200);
                    }}
                    disabled={!selectedPeriod || !selectedCourseId || loadingSchedules || schedulesForSelectedCourse.length === 0}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-8"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                    🔍
                  </span>
                </div>

                {isClassDropdownOpen && !loadingSchedules && selectedPeriod && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredSchedules.length > 0 ? (
                      filteredSchedules.map(schedule => (
                        <div
                          key={schedule.id}
                          className={`px-3 py-2 cursor-pointer hover:bg-blue-50 ${selectedSchedule === schedule.id ? 'bg-blue-100' : ''}`}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setSelectedSchedule(schedule.id);
                            setClassSearchTerm('');
                            setIsClassDropdownOpen(false);
                          }}
                        >
                          <div className="font-medium">
                            {schedule.sessionNumber && schedule.totalSessions && (
                              <span className="inline-block bg-blue-100 text-blue-700 text-xs font-bold rounded px-1.5 py-0.5 mr-1.5">
                                Clase {schedule.sessionNumber} de {schedule.totalSessions}
                              </span>
                            )}
                            {schedule.courseName}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateToDDMMYYYY(schedule.startDate)} | {schedule.dia} {schedule.horaInicio}-{schedule.horaFin} | {schedule.professorName || 'Sin profesor'}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-3 py-2 text-gray-500 text-sm">
                        No se encontraron clases con "{classSearchTerm}"
                      </div>
                    )}
                  </div>
                )}

                {selectedSchedule && !isClassDropdownOpen && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSchedule(null);
                      setClassSearchTerm('');
                    }}
                    className="absolute right-8 top-[38px] text-gray-400 hover:text-red-500"
                    title="Limpiar selección"
                  >
                    ✕
                  </button>
                )}

                {loadingSchedules && (
                  <p className="text-sm text-gray-500 mt-1">Cargando clases...</p>
                )}
                {selectedPeriod && !loadingSchedules && schedules.length === 0 && (
                  <p className="text-sm text-yellow-600 mt-1">No hay clases programadas para este grupo</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  4. Fecha
                </label>
                <input
                  type="date"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  disabled={!selectedSchedule}
                />
              </div>
            </div>

            {selectedPeriodData && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border">
                <p className="text-sm font-medium text-gray-700">
                  Grupo: {selectedPeriodData.name} - {periodTypeLabels[selectedPeriodData.type] || selectedPeriodData.type}
                </p>
                {selectedPeriodData.startDate && selectedPeriodData.endDate && (
                  <p className="text-sm text-gray-600">
                    Período: {selectedPeriodData.startDate} al {selectedPeriodData.endDate}
                  </p>
                )}
              </div>
            )}

            {selectedScheduleData && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                {selectedScheduleData.sessionNumber && selectedScheduleData.totalSessions && (
                  <div className="mb-2">
                    <span className="inline-block bg-blue-100 text-blue-700 text-sm font-bold rounded-lg px-3 py-1">
                      Clase {selectedScheduleData.sessionNumber} de {selectedScheduleData.totalSessions} clases
                    </span>
                  </div>
                )}
                <p className="text-sm text-gray-700">
                  <strong>Materia:</strong> {selectedScheduleData.courseName} (
                  {selectedScheduleData.courseCode})
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Programa:</strong> {selectedScheduleData.programName}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Aula:</strong> {selectedScheduleData.classroomName}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Profesor:</strong> {selectedScheduleData.professorName}
                </p>
              </div>
            )}
          </div>

          {selectedSchedule && (
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                  Lista de Estudiantes ({students.length})
                </h2>
                <Button onClick={handleSaveAttendance} disabled={saving || loading}>
                  {saving ? 'Guardando...' : 'Guardar Asistencia'}
                </Button>
              </div>

              {loading ? (
                <p className="text-center py-8 text-gray-500">Cargando estudiantes...</p>
              ) : students.length === 0 ? (
                <p className="text-center py-8 text-gray-500">
                  No hay estudiantes inscritos en este programa
                </p>
              ) : (
                <div className="w-full">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          NUI
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                          Nombre
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          Presente
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                          Ausente
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {[...students]
                        .sort((a, b) => {
                          const nuiComp = a.nui.localeCompare(b.nui, 'es', { numeric: true, sensitivity: 'base' });
                          if (nuiComp !== 0) return nuiComp;
                          const apComp = a.apellidoPaterno.localeCompare(b.apellidoPaterno, 'es', { sensitivity: 'base' });
                          if (apComp !== 0) return apComp;
                          const amComp = (a.apellidoMaterno || '').localeCompare(b.apellidoMaterno || '', 'es', { sensitivity: 'base' });
                          if (amComp !== 0) return amComp;
                          return a.nombre.localeCompare(b.nombre, 'es', { sensitivity: 'base' });
                        })
                        .map(student => {
                        const fullName = [student.apellidoPaterno, student.apellidoMaterno, student.nombre].filter(Boolean).join(' ');
                        const currentStatus = student.attendance?.status || 'presente';

                        return (
                          <tr key={student.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 text-sm text-gray-900">{student.nui}</td>
                            <td className="px-6 py-4 text-sm text-gray-900">{fullName}</td>
                            <td className="px-6 py-4 text-center">
                              <input
                                type="radio"
                                name={`attendance-${student.id}`}
                                checked={currentStatus === 'presente'}
                                onChange={() => handleAttendanceChange(student.id, 'presente')}
                                className="w-4 h-4 text-green-600"
                              />
                            </td>
                            <td className="px-6 py-4 text-center">
                              <input
                                type="radio"
                                name={`attendance-${student.id}`}
                                checked={currentStatus === 'ausente'}
                                onChange={() => handleAttendanceChange(student.id, 'ausente')}
                                className="w-4 h-4 text-red-600"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600">Alertas Totales</h3>
              <p className="text-3xl font-bold text-gray-900 mt-2">{alerts.length}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600">Alertas Críticas</h3>
              <p className="text-3xl font-bold text-red-600 mt-2">
                {alerts.filter(a => a.absentCount >= 5).length}
              </p>
              <p className="text-xs text-gray-500 mt-1">≥ 5 faltas</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-600">Alertas de Advertencia</h3>
              <p className="text-3xl font-bold text-yellow-600 mt-2">
                {alerts.filter(a => a.absentCount >= 3 && a.absentCount < 5).length}
              </p>
              <p className="text-xs text-gray-500 mt-1">3-4 faltas</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-semibold">Alertas de Ausentismo</h2>
            </div>
            <div className="p-6">
              {alerts.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No hay alertas de ausentismo</p>
                  <p className="text-gray-400 text-sm mt-2">
                    Todos los estudiantes tienen buen registro de asistencia
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {alerts
                    .sort((a, b) => b.absentCount - a.absentCount)
                    .map(alert => {
                      const level = getAlertLevel(alert.absentCount);
                      return (
                        <div
                          key={alert.alumnoId}
                          className={`border-l-4 rounded-lg p-4 ${alertStyles[level]}`}
                        >
                          <div className="flex justify-between items-start">
                            <div className="flex items-start gap-3">
                              <span className="text-2xl">{alertIcons[level]}</span>
                              <div>
                                <h3 className="font-semibold">{alert.alumnoNombre}</h3>
                                <p className="text-sm">NUI: {alert.alumnoNui}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-2xl">{alert.absentCount}</p>
                              <p className="text-xs">faltas</p>
                            </div>
                          </div>
                          <p className="text-sm mt-2">
                            Última falta:{' '}
                            {new Date(alert.lastAbsenceDate).toLocaleDateString('es-MX', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
